import { useCallback, useEffect, useRef, useState } from 'react';
import {
  salaDesafiar,
  salaEntrar,
  salaObterPartida,
  salaPing,
  salaResponder,
  salaSair
} from '../lib/api';
import { obterClienteSalaId } from '../lib/storage';
import type { DesafioSala, PartidaOnlineEstado, PartidaPublicaResumo, PresencaSala } from '../types';
import { Icone } from './Icone';

interface Props {
  nomeJogador: string;
  aoVoltar: () => void;
  /** Jogador (ou espectador se apenasAssistir). */
  aoIniciarOnline: (partida: PartidaOnlineEstado, clienteId: string, apenasAssistir?: boolean) => void;
}

export function Sala({ nomeJogador, aoVoltar, aoIniciarOnline }: Props) {
  const clienteId = useRef(obterClienteSalaId());
  const [sala, setSala] = useState<PresencaSala[]>([]);
  const [eu, setEu] = useState<PresencaSala | null>(null);
  const [desafios, setDesafios] = useState<DesafioSala[]>([]);
  const [partidasPublicas, setPartidasPublicas] = useState<PartidaPublicaResumo[]>([]);
  const [aviso, setAviso] = useState<string | null>(null);
  const [conectado, setConectado] = useState(false);
  const [permitirEspectadoresDesafio, setPermitirEspectadoresDesafio] = useState(false);
  const [permitirEspectadoresAceite, setPermitirEspectadoresAceite] = useState(false);
  const iniciouRef = useRef(false);

  const aplicarEstado = useCallback(
    (dados: {
      cliente: PresencaSala;
      sala: PresencaSala[];
      desafios?: DesafioSala[];
      partida?: PartidaOnlineEstado;
      partidasPublicas?: PartidaPublicaResumo[];
    }) => {
      setEu(dados.cliente);
      setSala(dados.sala);
      if (dados.desafios) setDesafios(dados.desafios);
      if (dados.partidasPublicas) setPartidasPublicas(dados.partidasPublicas);
      if (dados.partida && dados.partida.status === 'ativa' && !iniciouRef.current) {
        iniciouRef.current = true;
        aoIniciarOnline(dados.partida, clienteId.current, false);
      }
    },
    [aoIniciarOnline]
  );

  useEffect(() => {
    let vivo = true;
    iniciouRef.current = false;
    void (async () => {
      try {
        const r = await salaEntrar(nomeJogador, clienteId.current);
        if (!vivo) return;
        clienteId.current = r.cliente.id;
        setConectado(true);
        aplicarEstado({ ...r, desafios: [], partidasPublicas: [] });
      } catch (e) {
        if (vivo) setAviso(e instanceof Error ? e.message : 'Não foi possível entrar na sala.');
      }
    })();
    const timer = window.setInterval(() => {
      void salaPing(clienteId.current)
        .then((r) => {
          if (!vivo) return;
          setConectado(true);
          aplicarEstado(r);
        })
        .catch((e) => {
          if (vivo) {
            setConectado(false);
            setAviso(e instanceof Error ? e.message : 'Conexão com a sala perdida.');
          }
        });
    }, 2000);
    return () => {
      vivo = false;
      window.clearInterval(timer);
      void salaSair(clienteId.current);
    };
  }, [nomeJogador, aplicarEstado]);

  async function desafiar(paraId: string) {
    setAviso(null);
    try {
      await salaDesafiar(clienteId.current, paraId, permitirEspectadoresDesafio);
      const r = await salaPing(clienteId.current);
      aplicarEstado(r);
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'Não foi possível desafiar.');
    }
  }

  async function responder(desafioId: string, aceitar: boolean) {
    setAviso(null);
    try {
      const r = await salaResponder(clienteId.current, desafioId, aceitar, aceitar ? permitirEspectadoresAceite : false);
      if (r.partida && aceitar) {
        iniciouRef.current = true;
        aoIniciarOnline(r.partida, clienteId.current, false);
        return;
      }
      const ping = await salaPing(clienteId.current);
      aplicarEstado(ping);
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'Não foi possível responder ao desafio.');
    }
  }

  async function assistir(partidaId: string) {
    setAviso(null);
    try {
      const partida = await salaObterPartida(partidaId, clienteId.current);
      if (partida.status !== 'ativa') {
        setAviso('Esta partida já terminou.');
        return;
      }
      if (!partida.permitirEspectadores) {
        setAviso('Os jogadores não liberaram espectadores.');
        return;
      }
      // Não assiste se for um dos jogadores
      if (partida.brancasId === clienteId.current || partida.pretasId === clienteId.current) {
        iniciouRef.current = true;
        aoIniciarOnline(partida, clienteId.current, false);
        return;
      }
      iniciouRef.current = true;
      aoIniciarOnline(partida, clienteId.current, true);
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'Não foi possível assistir.');
    }
  }

  const outros = sala.filter((p) => p.id !== clienteId.current);
  const desafioRecebido = desafios.find((d) => d.paraId === clienteId.current);
  const desafioEnviado = desafios.find((d) => d.deId === clienteId.current);
  // Partidas que não sou eu jogando
  const paraAssistir = partidasPublicas.filter(
    (p) => p.status === 'ativa'
  );

  return (
    <main className="tela-sala">
      <header className="cabecalho-interno">
        <button type="button" className="botao-voltar" onClick={aoVoltar}>
          <Icone nome="voltar" /> Voltar
        </button>
        <div className="monograma-interno">
          <span>XADREZ</span>
          <small>SALA MULTIPLAYER</small>
        </div>
      </header>
      <section className="corpo-sala">
        <div className="intro-sala">
          <small>{conectado ? 'CONECTADO' : 'RECONECTANDO…'}</small>
          <h1>Sala de espera</h1>
          <p>
            Você está como <strong>{nomeJogador}</strong>. Desafie alguém, marque se a partida pode ser assistida, ou
            assista a um jogo aberto.
          </p>
        </div>
        {aviso && (
          <div className="aviso-sala" role="status">
            {aviso}
            <button type="button" onClick={() => setAviso(null)} aria-label="Fechar">
              <Icone nome="fechar" tamanho={16} />
            </button>
          </div>
        )}

        <label className="opcao-espectadores">
          <input
            type="checkbox"
            checked={permitirEspectadoresDesafio}
            onChange={(e) => setPermitirEspectadoresDesafio(e.target.checked)}
          />
          <span>
            <strong>Permitir espectadores</strong>
            <small>Ao desafiar, a partida só fica pública se o adversário também marcar ao aceitar.</small>
          </span>
        </label>

        {desafioRecebido && (
          <div className="cartao-desafio">
            <strong>{desafioRecebido.deNome}</strong> te desafiou.
            {desafioRecebido.permitirEspectadores ? (
              <p className="nota-desafio">O desafiante quer liberar a partida para espectadores.</p>
            ) : (
              <p className="nota-desafio">Partida privada (sem espectadores), a menos que ambos marquem a opção.</p>
            )}
            <label className="opcao-espectadores compacta">
              <input
                type="checkbox"
                checked={permitirEspectadoresAceite}
                onChange={(e) => setPermitirEspectadoresAceite(e.target.checked)}
              />
              <span>
                <strong>Também permito espectadores</strong>
                <small>Só fica aberta se os dois marcarem.</small>
              </span>
            </label>
            <div className="acoes-desafio">
              <button type="button" className="botao-principal" onClick={() => void responder(desafioRecebido.id, true)}>
                Aceitar
              </button>
              <button type="button" className="botao-contorno" onClick={() => void responder(desafioRecebido.id, false)}>
                Recusar
              </button>
            </div>
          </div>
        )}
        {desafioEnviado && !desafioRecebido && (
          <div className="cartao-desafio pendente">
            Aguardando <strong>{desafioEnviado.paraNome}</strong> responder…
            {desafioEnviado.permitirEspectadores && <small className="nota-desafio">Você pediu partida aberta a espectadores.</small>}
            <button type="button" className="botao-textual" onClick={() => void responder(desafioEnviado.id, false)}>
              Cancelar
            </button>
          </div>
        )}

        {paraAssistir.length > 0 && (
          <div className="lista-partidas-publicas">
            <header>
              <span>Partidas para assistir</span>
              <small>{paraAssistir.length} aberta{paraAssistir.length === 1 ? '' : 's'}</small>
            </header>
            {paraAssistir.map((p) => (
              <div className="partida-publica-item" key={p.id}>
                <div>
                  <strong>
                    {p.nomeBrancas} <em>vs</em> {p.nomePretas}
                  </strong>
                  <small>
                    {p.lances} lance{p.lances === 1 ? '' : 's'} · vez das {p.turno === 'w' ? 'brancas' : 'pretas'}
                  </small>
                </div>
                <button type="button" className="botao-contorno" onClick={() => void assistir(p.id)}>
                  <Icone nome="olho" tamanho={16} /> Assistir
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="lista-presenca">
          <header>
            <span>Na sala</span>
            <small>
              {outros.length + 1} conectado{outros.length === 0 ? '' : 's'}
            </small>
          </header>
          <div className="presenca-eu">
            <i className="ponto-online" />
            <div>
              <strong>{eu?.nome ?? nomeJogador}</strong>
              <small>você · {eu?.status === 'sala' ? 'livre' : eu?.status ?? '…'}</small>
            </div>
          </div>
          {outros.length === 0 ? (
            <div className="sala-vazia">
              <Icone nome="pessoas" tamanho={32} />
              <p>
                Ninguém mais na sala ainda. Deixe esta página aberta e peça a um amigo para entrar em Xadrez → Sala
                multiplayer no mesmo servidor.
              </p>
            </div>
          ) : (
            outros.map((p) => (
              <div className="presenca-item" key={p.id}>
                <i className={p.status === 'sala' ? 'ponto-online' : 'ponto-ocupado'} />
                <div>
                  <strong>{p.nome}</strong>
                  <small>{p.status === 'sala' ? 'livre' : p.status === 'ocupado' ? 'ocupado' : 'em partida'}</small>
                </div>
                <button
                  type="button"
                  className="botao-contorno"
                  disabled={p.status !== 'sala' || eu?.status !== 'sala'}
                  onClick={() => void desafiar(p.id)}
                >
                  Desafiar
                </button>
              </div>
            ))
          )}
        </div>
      </section>
    </main>
  );
}
